import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ClockCounterClockwise, Trash, Globe, Code, CircleNotch, ArrowRight } from "@phosphor-icons/react";
import { listRuns, deleteRun } from "@/lib/api";

const StatusPill = ({ status }) => {
  const map = {
    completed: "bg-[hsl(160_84%_39%/0.12)] text-[hsl(160_70%_38%)]",
    failed: "bg-[hsl(0_100%_68%/0.12)] text-[hsl(0_75%_55%)]",
  };
  const running = !["completed", "failed"].includes(status);
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize ${running ? "bg-primary/10 text-primary" : map[status]}`}>
      {running && <CircleNotch size={11} weight="bold" className="animate-spin" />}
      {status}
    </span>
  );
};

export const HistoryList = ({ onOpen, refreshKey }) => {
  const [runs, setRuns] = useState(null);

  const load = async () => {
    try { setRuns(await listRuns()); } catch { setRuns([]); }
  };
  useEffect(() => { load(); }, [refreshKey]);

  const remove = async (e, id) => {
    e.stopPropagation();
    await deleteRun(id);
    toast.success("Report deleted");
    load();
  };

  if (runs === null) {
    return <div className="py-20 text-center text-muted-foreground"><CircleNotch size={28} className="mx-auto animate-spin" /></div>;
  }

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6 flex items-center gap-2">
        <ClockCounterClockwise size={22} weight="bold" className="text-primary" />
        <h2 className="font-head text-2xl font-bold tracking-tight">Test history</h2>
      </div>

      {runs.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border bg-card py-16 text-center">
          <p className="text-muted-foreground">No test runs yet. Launch your first autonomous test.</p>
        </div>
      )}

      <div className="space-y-3">
        {runs.map((r, i) => (
          <motion.button
            key={r.id}
            data-testid={`history-item-${i}`}
            onClick={() => onOpen(r.id)}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
            className="group flex w-full items-center gap-4 rounded-2xl border border-border bg-card p-4 text-left transition-all hover:border-primary/50 hover:shadow-md"
          >
            <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${r.target_type === "api" ? "bg-primary/10 text-primary" : "bg-[hsl(160_84%_39%/0.12)] text-[hsl(160_70%_40%)]"}`}>
              {r.target_type === "api" ? <Code size={20} weight="bold" /> : <Globe size={20} weight="bold" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate font-head font-semibold">{r.target_title || r.name}</div>
              <div className="truncate font-mono text-xs text-muted-foreground">{r.source || r.base_url}</div>
            </div>
            {r.stats?.total > 0 && (
              <div className="hidden text-right sm:block">
                <div className="font-head text-sm font-bold">{r.stats.pass_rate}%</div>
                <div className="text-[11px] text-muted-foreground">{r.stats.passed}/{r.stats.total} passed</div>
              </div>
            )}
            <StatusPill status={r.status} />
            <div className="hidden text-xs text-muted-foreground md:block">{new Date(r.created_at).toLocaleDateString()}</div>
            <span onClick={(e) => remove(e, r.id)} data-testid={`delete-${i}`}
              className="rounded-lg p-2 text-muted-foreground opacity-0 transition-all hover:bg-accent hover:text-[hsl(0_75%_58%)] group-hover:opacity-100">
              <Trash size={16} />
            </span>
            <ArrowRight size={16} className="text-muted-foreground transition-transform group-hover:translate-x-1" />
          </motion.button>
        ))}
      </div>
    </div>
  );
};
